import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { MEDICAL_FAQ_CATEGORIES } from "@/content/medical-corpus/faq-defs";

export const Route = createFileRoute("/en/medical/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/medical-corpus");
    return { faqs: m.MEDICAL_FAQ_EN };
  },
  component: MedicalFaqHubEn,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Medical Documents, Health Insurance & Treatment Visas — ${faqs.length} FAQs`,
        description:
          "Thai medical certificates in Medical Council format, medical records and discharge summaries, ICVP vaccination certificates, translation and MFA legalization of medical papers, medical treatment visas and carers, the health insurance limits required by O-A, O-X and LTR visas, cross-border insurance claims, birth and death documents, and medical powers of attorney.",
        path: "/en/medical/faq",
        type: "article",
        locale: "en_US",
        alternates: [{ lang: "th", path: "/medical/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Medical Documents FAQ", url: `${site}/en/medical/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/medical/faq")) },
      ],
    };
  },
});

function MedicalFaqHubEn() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Medical Documents FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Medical Documents, Health Insurance and Treatment Visas in Thailand — {faqs.length} FAQs
        </h1>
        <p className="mb-6 text-muted-foreground">
          Answers reference the Medical Council of Thailand (tmc.or.th), the Department of Health Service Support
          (hss.moph.go.th), the Personal Data Protection Act B.E. 2562, the Civil Registration Act B.E. 2534, the
          National Health Act B.E. 2550, the Department of Consular Affairs (consular.mfa.go.th), the Immigration
          Bureau (immigration.go.th), the Office of Insurance Commission (oic.or.th) and the WHO International
          Health Regulations 2005. This is general information; rules change, so reconfirm with the authority or
          hospital before you file.
        </p>
        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>ภาษาไทย:</strong>{" "}
          <a href="/medical/faq" className="text-primary underline">FAQ เอกสารการแพทย์ ประกันสุขภาพ และวีซ่ารักษาพยาบาล →</a>
        </div>
        <section aria-label="Topics" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">Browse by topic</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {MEDICAL_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/en/medical/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm">
                <div className="mb-1 font-semibold">{c.titleEn}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descEn}</div>
              </a>
            ))}
          </div>
        </section>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">{i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">Talk to our team</h2>
          <p className="text-sm text-muted-foreground">
            Ask about document lists, certified medical translations, legalization and insurance claim bundles by phone, LINE or email,
            Monday to Saturday 9:00–18:00.
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">Contact us</a>
        </section>
        <FaqSourceNote topic="medical" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}
